import { useState, useEffect, useRef } from "react";
import { BrandMark } from "@/components/BrandMark";
import HeightWeightPicker from "@/components/HeightWeightPicker";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PaperPlaneRight } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BG = "https://images.unsplash.com/photo-1586010339640-0cee8a975977?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxzZXJlbmUlMjBtaW5pbWFsaXN0JTIwbmF0dXJlJTIwd2FybSUyMGxpZ2h0fGVufDB8fHx8MTc4MzA3MDg3Mnww&ixlib=rb-4.1.0&q=85";

export default function Onboarding() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/onboarding/messages").then(({ data }) => {
      setMessages(data.messages);
      setDone(data.done);
    });
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const lastMsg = messages[messages.length - 1];
  const lastOptions = (lastMsg?.role === "assistant" && lastMsg.options) || [];
  const lastInputType = (lastMsg?.role === "assistant" && lastMsg.input_type) || "";

  const send = async (preset) => {
    const text = (preset ?? input).trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post("/onboarding/message", { text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, options: data.options || [], input_type: data.input_type || "" }]);
      if (data.done) {
        setDone(true);
        await checkAuth();
        setTimeout(() => navigate("/app", { replace: true }), 1800);
      }
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I lost that. Could you say it again?" }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="h-screen relative flex flex-col overflow-hidden bg-bg" style={{ height: "100dvh" }}>
      <div className="absolute inset-0" style={{ background: `url(${BG}) center/cover` }} />
      <div className="absolute inset-0 backdrop-blur-2xl" style={{ backgroundColor: "var(--bg)", opacity: 0.93 }} />
      <div className="relative z-10 flex flex-col h-full min-h-0 max-w-2xl w-full mx-auto px-4">
        <div className="flex items-center gap-2 py-6">
          <BrandMark size={32} />
          <span className="font-display text-lg font-semibold tracking-tight">Ikavio</span>
          <span className="ml-auto label-eyebrow">Getting to know you</span>
        </div>

        <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto scroll-thin space-y-4 py-4" data-testid="onboarding-messages">
          <AnimatePresence initial={false}>
            {messages.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-5 py-3.5 text-[15px] leading-relaxed ${
                    m.role === "user"
                      ? "bg-brand text-white rounded-3xl rounded-br-md"
                      : "bg-surface text-ink border border-border rounded-3xl rounded-bl-md shadow-sm"
                  }`}
                >
                  {m.content}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          {sending && (
            <div className="flex justify-start">
              <div className="bg-surface border border-border rounded-3xl rounded-bl-md px-5 py-4 flex gap-1.5">
                {[0, 1, 2].map((d) => (
                  <span key={d} className="w-2 h-2 rounded-full bg-muted2 typing-dot" style={{ animationDelay: `${d * 0.2}s` }} />
                ))}
              </div>
            </div>
          )}
          {!sending && !done && lastInputType === "height_weight" && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
              <HeightWeightPicker onConfirm={(text) => send(text)} disabled={sending} />
            </motion.div>
          )}
          {!sending && !done && !lastInputType && lastOptions.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="flex flex-wrap gap-2 justify-end" data-testid="onboarding-options">
              {lastOptions.map((opt) => (
                <button
                  key={opt}
                  data-testid={`onboarding-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => send(opt)}
                  className="bg-surface text-brand border border-brand/40 rounded-full px-4 py-2.5 text-sm font-medium hover:bg-brand hover:text-white active:scale-95 transition-all shadow-sm"
                >
                  {opt}
                </button>
              ))}
            </motion.div>
          )}
        </div>

        <div className="py-5">
          {done ? (
            <div className="text-center bg-surface border border-border rounded-3xl py-5 px-6 shadow-sm">
              <p className="font-display text-lg">Your Digital Twin is ready ✦</p>
              <p className="text-sm text-muted2 mt-1">Taking you to your dashboard…</p>
            </div>
          ) : (
            <div className="flex items-end gap-2 bg-surface border border-border rounded-3xl p-2 pl-5 shadow-sm">
              <textarea
                data-testid="onboarding-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                rows={1}
                placeholder={lastOptions.length ? "Or type your own…" : "Type your answer…"}
                className="flex-1 min-w-0 bg-transparent text-ink resize-none outline-none text-[15px] py-2.5 max-h-32"
              />
              <button
                data-testid="onboarding-send-btn"
                onClick={() => send()}
                disabled={sending || !input.trim()}
                className="w-11 h-11 rounded-full bg-brand text-white flex items-center justify-center hover:bg-ink transition-colors disabled:opacity-40 shrink-0"
              >
                <PaperPlaneRight size={18} weight="fill" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
